/**
 * WidgetBoundary — React class error boundary for dashboard widgets.
 *
 * Wraps a single chart or panel so that a render crash (e.g. a Recharts stack
 * triggered by unexpected data) is caught here and replaced by a token-pure
 * fallback card. The rest of the dashboard continues to render normally.
 *
 * Usage:
 *   <WidgetBoundary label="Work items by status">
 *     <CrStatusDonut data={…} />
 *   </WidgetBoundary>
 *
 * On error the fallback:
 *   • logs the caught error to the console with a widget-scoped prefix so the
 *     culprit is immediately identifiable.
 *   • renders a surface card (matching sibling widget chrome) with a human-
 *     readable message and a small mono hint.
 *   • does NOT re-throw — the crash is fully contained here.
 */
import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { sectionCardStyle } from './chartTokens';

// ---------------------------------------------------------------------------
// Props / State
// ---------------------------------------------------------------------------

interface Props {
  /** Human-readable widget name shown in the fallback and the console log. */
  label: string;
  children: ReactNode;
  /** Minimum height of the fallback card in px (default 200). */
  minHeight?: number;
}

interface State {
  hasError: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export class WidgetBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[dashboard-widget:' + this.props.label + ']', error, info);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const minHeight = this.props.minHeight ?? 200;
      return (
        <div
          style={sectionCardStyle({
            minHeight,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 6,
          })}
        >
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: 'var(--text-low)',
              margin: 0,
            }}
          >
            {this.props.label} couldn&apos;t be displayed.
          </p>
          <p
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--text-low)',
              margin: 0,
            }}
          >
            This widget hit an error — the rest of the dashboard is unaffected.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
